import React, { useCallback } from 'react';
import { HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { closeAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import { useDispatch } from 'react-redux';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider/context/IntegrationsProvider';
import { useAppSelector } from 'state/hooks';
import { useValidateAppointmentDraft } from '../hooks/useAppointmentValidation';
import { useSaveAppointmentDraft } from '../hooks/useSaveAppointment';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';

export const AppointmentModalFooter = (): JSX.Element => {
  const { appointmentId } = useAppSelector((state) => state.appointmentsModal);
  const { recapNotes } = useAppSelector((state) => state.appointmentsModal.appointmentDraft);
  const { saveAppointmentDraft, cancelAppointment, isSavingChanges, isDeletingAppointment } = useSaveAppointmentDraft();
  const { validateAppointmentDraft } = useValidateAppointmentDraft();
  const dispatch = useDispatch();
  const { isAppointmentEditWritebackSupported } = useIntegrationsProvider();
  const { currentClinic } = useClinicUser();
  const { isExportToPimsSupported, primaryIntegrationName } = useIntegrationsProvider();
  const isAiRecapWritebackEnabled = isExportToPimsSupported && currentClinic?.clinicSetting?.aiRecapWritebackEnabled;
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);

  const onDeleteAppointment = useCallback(async () => {
    if (!appointmentId) return;
    await cancelAppointment(appointmentId);
    dispatch(closeAppointmentModal());
  }, [appointmentId, cancelAppointment, dispatch]);

  const onSaveChanges = useCallback(async () => {
    const appointmentErrors = validateAppointmentDraft();

    if (!appointmentErrors) {
      await saveAppointmentDraft();
      dispatch(closeAppointmentModal());
    }
  }, [dispatch, saveAppointmentDraft, validateAppointmentDraft]);

  const onCloseModal = useCallback(() => {
    dispatch(closeAppointmentModal());
  }, [dispatch]);

  return (
    <VStack w="100%" p={4} boxShadow={isStandardClinic ? 'mdR' : undefined}>
      {!isAppointmentEditWritebackSupported && (
        <Alert
          status="warning"
          description={
            !recapNotes || !isAiRecapWritebackEnabled
              ? `Changes to this appointment will not be reflected in ${primaryIntegrationName}.`
              : `AI Recap Notes and any edits made to it will sync to ${primaryIntegrationName} nightly. Changes to other appointment details will not be reflected in your PIMS.`
          }
          hideCloseButton
        />
      )}
      <HStack w="100%" justify="space-between">
        {appointmentId ? (
          <Button
            variant="primaryDestructive"
            onClick={onDeleteAppointment}
            isLoading={isDeletingAppointment}
            data-testid="appointment-delete-button"
          >
            Delete Appointment
          </Button>
        ) : (
          <Button variant="secondary" onClick={onCloseModal}>
            Cancel
          </Button>
        )}
        <Button onClick={onSaveChanges} isLoading={isSavingChanges} data-testid="appointment-submit-button">
          {appointmentId ? 'Update' : 'Create'} Appointment
        </Button>
      </HStack>
    </VStack>
  );
};
